/**
 * Web Series Service
 * Handles all web series related API operations
 */

import api from '../helpers/axios';
import {
  WebSeriesMaster,
  WebSeriesEpisode,
  WebSeriesCategoryMapping,
  EpisodeArticleMapping,
  ArticleType,
  CreateSeriesRequest,
  UpdateSeriesRequest,
  CreateEpisodeRequest,
  UpdateEpisodeRequest,
  AddArticleToEpisodeRequest,
  UpdateArticleMappingRequest,
  ReorderArticlesRequest,
  SeriesFilterOptions,
  PaginationOptions,
  PaginatedResponse,
  BulkStatusUpdateRequest,
  BulkDeactivateRequest,
  SeriesViewStatistics,
  SeriesCodeValidation,
  EpisodeNumberValidation,
  FileUploadResponse,
  EpisodeReorderRequest,
  SeriesStatus
} from '../types/webSeriesTypes';

class WebSeriesService {
  private readonly API_BASE = '/api/admin/web-series';
  private readonly PUBLIC_API_BASE = '/api/web-series';

  // ==================== SERIES MANAGEMENT ====================

  /**
   * Create a new web series
   */
  async createSeries(seriesData: CreateSeriesRequest): Promise<WebSeriesMaster> {
    try {
      console.log('WebSeriesService: Creating series with data:', seriesData);
      console.log('API URL:', `${this.API_BASE}`);
      
      const response = await api.post(this.API_BASE, seriesData);
      
      console.log('WebSeriesService: Series creation response:', response);
      console.log('WebSeriesService: Response data:', response.data);
      
      return response.data.series;
    } catch (error) {
      console.error('WebSeriesService: Error creating series:', error);
      
      // Enhanced error logging
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        console.error('Response status:', axiosError.response?.status);
        console.error('Response data:', axiosError.response?.data);
        console.error('Response headers:', axiosError.response?.headers);
      }
      
      throw new Error('Failed to create series');
    }
  }

  /**
   * Update an existing web series
   */
  async updateSeries(seriesData: UpdateSeriesRequest): Promise<WebSeriesMaster> {
    try {
      const response = await api.put(`${this.API_BASE}/${seriesData.seriesId}`, seriesData);
      return response.data.series;
    } catch (error) {
      console.error('Error updating series:', error);
      throw new Error('Failed to update series');
    }
  }

  /**
   * Get series by ID
   */
  async getSeriesById(seriesId: number): Promise<WebSeriesMaster> {
    try {
      const response = await api.get(`${this.API_BASE}/${seriesId}`);
      return response.data.series;
    } catch (error) {
      console.error('Error fetching series:', error);
      throw new Error('Failed to fetch series');
    }
  }

  /**
   * Get all series with pagination and filtering
   */
  async getAllSeries(
    pagination: PaginationOptions = {},
    filters: SeriesFilterOptions = {}
  ): Promise<PaginatedResponse<WebSeriesMaster>> {
    try {
      const params = {
        page: (pagination.page ? pagination.page - 1 : 0), // Convert from 1-based to 0-based pagination
        size: pagination.size || 10,
        sortBy: pagination.sortBy || 'updatedAt',
        sortDir: pagination.sortDir || 'desc',
        ...filters
      };

      const response = await api.get(this.API_BASE, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching all series:', error);
      throw new Error('Failed to fetch series');
    }
  }

  /**
   * Search series by query string (Admin API)
   * Used for autocomplete and dynamic search in admin panels
   */
  async searchSeriesAdmin(
    query: string,
    publishedOnly: boolean = false,
    pagination: PaginationOptions = {}
  ): Promise<PaginatedResponse<WebSeriesMaster>> {
    try {
      const params = {
        query,
        publishedOnly,
        page: (pagination.page ? pagination.page - 1 : 0),
        size: pagination.size || 10
      };

      const response = await api.get(`${this.API_BASE}/search`, { params });
      
      // Transform response to match PaginatedResponse structure
      return {
        content: response.data.content || [],
        totalPages: response.data.totalPages || 0,
        totalElements: response.data.totalElements || 0,
        currentPage: response.data.currentPage || 0,
        size: params.size,
        hasNext: (response.data.currentPage || 0) < (response.data.totalPages || 1) - 1,
        hasPrevious: (response.data.currentPage || 0) > 0
      };
    } catch (error) {
      console.error('Error searching series (admin):', error);
      throw new Error('Failed to search series');
    }
  }

  /**
   * Delete a series (soft delete)
   */
  async deleteSeries(seriesId: number, deletedBy: string = 'admin'): Promise<void> {
    try {
      await api.delete(`${this.API_BASE}/${seriesId}`, {
        params: { deletedBy }
      });
    } catch (error) {
      console.error('Error deleting series:', error);
      throw new Error('Failed to delete series');
    }
  }

  /**
   * Clone series structure
   */
  async cloneSeriesStructure(
    seriesId: number,
    newSeriesName: string,
    newSeriesCode: string,
    createdBy: string = 'admin'
  ): Promise<WebSeriesMaster> {
    try {
      const response = await api.post(`${this.API_BASE}/${seriesId}/clone`, null, {
        params: {
          newSeriesName,
          newSeriesCode,
          createdBy
        }
      });
      return response.data.newSeries;
    } catch (error) {
      console.error('Error cloning series:', error);
      throw new Error('Failed to clone series');
    }
  }

  // ==================== EPISODE MANAGEMENT ====================

  /**
   * Add episode to series
   */
  async addEpisodeToSeries(seriesId: number, episodeData: CreateEpisodeRequest): Promise<WebSeriesEpisode> {
    try {
      const response = await api.post(`${this.API_BASE}/${seriesId}/episodes`, episodeData);
      return response.data.episode;
    } catch (error) {
      console.error('Error adding episode:', error);
      throw new Error('Failed to add episode');
    }
  }

  /**
   * Update episode
   */
  async updateEpisode(episodeData: UpdateEpisodeRequest): Promise<WebSeriesEpisode> {
    try {
      const response = await api.put(`${this.API_BASE}/episodes/${episodeData.episodeId}`, episodeData);
      return response.data.episode;
    } catch (error) {
      console.error('Error updating episode:', error);
      throw new Error('Failed to update episode');
    }
  }

  /**
   * Get episode by ID
   */
  async getEpisodeById(episodeId: number): Promise<WebSeriesEpisode> {
    try {
      const response = await api.get(`${this.API_BASE}/episodes/${episodeId}`);
      return response.data.episode;
    } catch (error) {
      console.error('Error fetching episode:', error);
      throw new Error('Failed to fetch episode');
    }
  }

  /**
   * Get all episodes for a series
   */
  async getEpisodesBySeries(seriesId: number): Promise<WebSeriesEpisode[]> {
    try {
      const response = await api.get(`${this.API_BASE}/${seriesId}/episodes/all`);
      return response.data.episodes;
    } catch (error) {
      console.error('Error fetching episodes:', error);
      throw new Error('Failed to fetch episodes');
    }
  }

  /**
   * Get all episodes for a series with metadata
   */
  async getAllEpisodesBySeries(seriesId: number): Promise<{ episodes: WebSeriesEpisode[]; count: number; seriesId: number }> {
    try {
      const response = await api.get(`${this.API_BASE}/${seriesId}/episodes/all`);
      return response.data;
    } catch (error) {
      console.error('Error fetching episodes:', error);
      throw new Error('Failed to fetch episodes');
    }
  }

  /**
   * Delete episode
   */
  async deleteEpisode(episodeId: number, deletedBy: string = 'admin'): Promise<void> {
    try {
      await api.delete(`${this.API_BASE}/episodes/${episodeId}`, {
        params: { deletedBy }
      });
    } catch (error) {
      console.error('Error deleting episode:', error);
      throw new Error('Failed to delete episode');
    }
  }

  /**
   * Reorder episodes
   */
  async reorderEpisodes(seriesId: number, reorderData: EpisodeReorderRequest): Promise<void> {
    try {
      await api.put(`${this.API_BASE}/${seriesId}/episodes/reorder`, reorderData);
    } catch (error) {
      console.error('Error reordering episodes:', error);
      throw new Error('Failed to reorder episodes');
    }
  }

  // ==================== CATEGORY MANAGEMENT ====================

  /**
   * Add category to series
   */
  async addCategoryToSeries(
    seriesId: number,
    categoryId: number,
    isPrimary: boolean = false
  ): Promise<WebSeriesCategoryMapping> {
    try {
      const response = await api.post(
        `${this.API_BASE}/${seriesId}/categories/${categoryId}`,
        null,
        { params: { isPrimary } }
      );
      return response.data.mapping;
    } catch (error) {
      console.error('Error adding category to series:', error);
      throw new Error('Failed to add category to series');
    }
  }

  /**
   * Remove category from series
   */
  async removeCategoryFromSeries(seriesId: number, categoryId: number): Promise<void> {
    try {
      await api.delete(`${this.API_BASE}/${seriesId}/categories/${categoryId}`);
    } catch (error) {
      console.error('Error removing category from series:', error);
      throw new Error('Failed to remove category from series');
    }
  }

  /**
   * Set primary category for series
   */
  async setPrimaryCategory(seriesId: number, categoryId: number): Promise<void> {
    try {
      await api.put(`${this.API_BASE}/${seriesId}/categories/${categoryId}/primary`);
    } catch (error) {
      console.error('Error setting primary category:', error);
      throw new Error('Failed to set primary category');
    }
  }

  /**
   * Get categories for series
   */
  async getSeriesCategories(seriesId: number): Promise<WebSeriesCategoryMapping[]> {
    try {
      const response = await api.get(`${this.API_BASE}/${seriesId}/categories`);
      return response.data.categories;
    } catch (error) {
      console.error('Error fetching series categories:', error);
      throw new Error('Failed to fetch series categories');
    }
  }

  // ==================== FILE UPLOAD ====================

  /**
   * Upload series thumbnail
   */
  async uploadSeriesThumbnail(seriesId: number, file: File): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`${this.API_BASE}/${seriesId}/thumbnail`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading series thumbnail:', error);
      throw new Error('Failed to upload series thumbnail');
    }
  }

  /**
   * Upload series banner
   */
  async uploadSeriesBanner(seriesId: number, file: File): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`${this.API_BASE}/${seriesId}/banner`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading series banner:', error);
      throw new Error('Failed to upload series banner');
    }
  }

  /**
   * Upload series trailer
   */
  async uploadSeriesTrailer(seriesId: number, file: File): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`${this.API_BASE}/${seriesId}/trailer`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading series trailer:', error);
      throw new Error('Failed to upload series trailer');
    }
  }

  /**
   * Upload episode thumbnail
   */
  async uploadEpisodeThumbnail(episodeId: number, file: File): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`${this.API_BASE}/episodes/${episodeId}/thumbnail`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading episode thumbnail:', error);
      throw new Error('Failed to upload episode thumbnail');
    }
  }

  /**
   * Upload episode file
   */
  async uploadEpisodeFile(episodeId: number, file: File): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`${this.API_BASE}/episodes/${episodeId}/file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading episode file:', error);
      throw new Error('Failed to upload episode file');
    }
  }

  // ==================== BULK OPERATIONS ====================

  /**
   * Update series status in bulk
   */
  async updateSeriesStatusBulk(request: BulkStatusUpdateRequest): Promise<number> {
    try {
      const response = await api.put(`${this.API_BASE}/bulk/status`, request);
      return response.data.updatedCount;
    } catch (error) {
      console.error('Error updating series status in bulk:', error);
      throw new Error('Failed to update series status in bulk');
    }
  }

  /**
   * Deactivate series in bulk
   */
  async deactivateSeriesBulk(request: BulkDeactivateRequest): Promise<number> {
    try {
      const response = await api.put(`${this.API_BASE}/bulk/deactivate`, request);
      return response.data.deactivatedCount;
    } catch (error) {
      console.error('Error deactivating series in bulk:', error);
      throw new Error('Failed to deactivate series in bulk');
    }
  }

  /**
   * Publish scheduled episodes
   */
  async publishScheduledEpisodes(): Promise<number> {
    try {
      const response = await api.post(`${this.API_BASE}/episodes/publish-scheduled`);
      return response.data.publishedCount;
    } catch (error) {
      console.error('Error publishing scheduled episodes:', error);
      throw new Error('Failed to publish scheduled episodes');
    }
  }

  // ==================== ANALYTICS ====================

  /**
   * Get series view statistics
   */
  async getSeriesViewStatistics(): Promise<SeriesViewStatistics[]> {
    try {
      const response = await api.get(`${this.API_BASE}/analytics/views`);
      return response.data.statistics;
    } catch (error) {
      console.error('Error fetching series view statistics:', error);
      throw new Error('Failed to fetch series view statistics');
    }
  }

  // ==================== VALIDATION ====================

  /**
   * Validate series code uniqueness
   */
  async validateSeriesCode(seriesCode: string, excludeSeriesId?: number): Promise<SeriesCodeValidation> {
    try {
      const params = excludeSeriesId ? { excludeSeriesId } : {};
      const response = await api.get(`${this.API_BASE}/validate/series-code/${seriesCode}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error validating series code:', error);
      throw new Error('Failed to validate series code');
    }
  }

  /**
   * Validate episode number uniqueness
   */
  async validateEpisodeNumber(
    seriesId: number,
    episodeNumber: number,
    seasonNumber: number = 1,
    excludeEpisodeId?: number
  ): Promise<EpisodeNumberValidation> {
    try {
      const params = {
        seasonNumber,
        ...(excludeEpisodeId && { excludeEpisodeId })
      };
      const response = await api.get(
        `${this.API_BASE}/${seriesId}/validate/episode-number/${episodeNumber}`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('Error validating episode number:', error);
      throw new Error('Failed to validate episode number');
    }
  }

  // ==================== PUBLIC API (for frontend consumption) ====================

  /**
   * Get published series (public API)
   */
  async getPublishedSeries(
    pagination: PaginationOptions = {},
    filters: SeriesFilterOptions = {}
  ): Promise<PaginatedResponse<WebSeriesMaster>> {
    try {
      const params = {
        page: (pagination.page ? pagination.page - 1 : 0), // Convert from 1-based to 0-based pagination
        size: pagination.size || 10,
        sortBy: pagination.sortBy || 'updatedAt',
        sortDir: pagination.sortDir || 'desc',
        ...filters,
        publishedOnly: true
      };

      const response = await api.get(this.PUBLIC_API_BASE, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching published series:', error);
      throw new Error('Failed to fetch published series');
    }
  }

  /**
   * Get featured series (public API)
   */
  async getFeaturedSeries(): Promise<WebSeriesMaster[]> {
    try {
      const response = await api.get(`${this.PUBLIC_API_BASE}/featured`);
      return response.data.series;
    } catch (error) {
      console.error('Error fetching featured series:', error);
      throw new Error('Failed to fetch featured series');
    }
  }

  /**
   * Get series by category (public API)
   */
  async getSeriesByCategory(
    categoryId: number,
    pagination: PaginationOptions = {}
  ): Promise<PaginatedResponse<WebSeriesMaster>> {
    try {
      const params = {
        page: (pagination.page ? pagination.page - 1 : 0), // Convert from 1-based to 0-based pagination
        size: pagination.size || 10,
        sortBy: pagination.sortBy || 'updatedAt',
        sortDir: pagination.sortDir || 'desc'
      };

      const response = await api.get(`${this.PUBLIC_API_BASE}/category/${categoryId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching series by category:', error);
      throw new Error('Failed to fetch series by category');
    }
  }

  /**
   * Get published episodes for series (public API)
   */
  async getPublishedEpisodes(seriesId: number): Promise<WebSeriesEpisode[]> {
    try {
      const response = await api.get(`${this.PUBLIC_API_BASE}/${seriesId}/episodes`);
      return response.data.episodes;
    } catch (error) {
      console.error('Error fetching published episodes:', error);
      throw new Error('Failed to fetch published episodes');
    }
  }

  /**
   * Search series (public API)
   */
  async searchSeries(
    searchText: string,
    pagination: PaginationOptions = {}
  ): Promise<PaginatedResponse<WebSeriesMaster>> {
    try {
      const params = {
        searchText,
        page: (pagination.page ? pagination.page - 1 : 0), // Convert from 1-based to 0-based pagination
        size: pagination.size || 10,
        sortBy: pagination.sortBy || 'relevance',
        sortDir: pagination.sortDir || 'desc'
      };

      const response = await api.get(`${this.PUBLIC_API_BASE}/search`, { params });
      return response.data;
    } catch (error) {
      console.error('Error searching series:', error);
      throw new Error('Failed to search series');
    }
  }

  /**
   * Track series view (public API)
   */
  async trackSeriesView(seriesId: number): Promise<void> {
    try {
      await api.post(`${this.PUBLIC_API_BASE}/${seriesId}/view`);
    } catch (error) {
      console.error('Error tracking series view:', error);
      // Don't throw error for analytics failures
    }
  }

  /**
   * Track episode view (public API)
   */
  async trackEpisodeView(episodeId: number): Promise<void> {
    try {
      await api.post(`${this.PUBLIC_API_BASE}/episodes/${episodeId}/view`);
    } catch (error) {
      console.error('Error tracking episode view:', error);
      // Don't throw error for analytics failures
    }
  }

  /**
   * Get Web Series data for a specific article
   */
  async getSeriesDataForArticle(articleId: number): Promise<{
    series: WebSeriesMaster | null;
    episode: WebSeriesEpisode | null;
  } | null> {
    try {
      const response = await api.get(`${this.PUBLIC_API_BASE}/episodes/article/${articleId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting series data for article:', error);
      return null;
    }
  }

  /**
   * Link an article to an episode (legacy method - use addArticleToEpisode instead)
   */
  async linkArticleToEpisode(episodeId: number, articleId: number, isMainArticle: boolean = true): Promise<void> {
    try {
      const response = await api.post(`${this.API_BASE}/episodes/${episodeId}/articles`, {
        articleId,
        isMainArticle
      });
      return response.data;
    } catch (error) {
      console.error('Error linking article to episode:', error);
      throw new Error('Failed to link article to episode');
    }
  }

  // ==================== COMPREHENSIVE EPISODE ARTICLE MANAGEMENT ====================

  /**
   * Add article to episode with full options
   */
  async addArticleToEpisode(episodeId: number, request: AddArticleToEpisodeRequest): Promise<EpisodeArticleMapping> {
    try {
      const response = await api.post(`${this.API_BASE}/episodes/${episodeId}/articles`, {
        ...request,
        createdBy: request.createdBy || 'admin'
      });
      return response.data.mapping;
    } catch (error) {
      console.error('Error adding article to episode:', error);
      throw new Error('Failed to add article to episode');
    }
  }

  /**
   * Get articles for episode
   */
  async getEpisodeArticles(episodeId: number, articleType?: ArticleType): Promise<{ articles: EpisodeArticleMapping[], count: number }> {
    try {
      const params = articleType ? { articleType } : {};
      const response = await api.get(`${this.API_BASE}/episodes/${episodeId}/articles`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching episode articles:', error);
      throw new Error('Failed to fetch episode articles');
    }
  }

  /**
   * Get main article for episode
   */
  async getMainArticleForEpisode(episodeId: number): Promise<EpisodeArticleMapping | null> {
    try {
      const response = await api.get(`${this.API_BASE}/episodes/${episodeId}/articles/main`);
      return response.data.mainArticle;
    } catch (error) {
      console.error('Error fetching main article for episode:', error);
      throw new Error('Failed to fetch main article');
    }
  }

  /**
   * Get supplementary articles for episode
   */
  async getSupplementaryArticles(episodeId: number): Promise<{ supplementaryArticles: EpisodeArticleMapping[], count: number }> {
    try {
      const response = await api.get(`${this.API_BASE}/episodes/${episodeId}/articles/supplementary`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplementary articles:', error);
      throw new Error('Failed to fetch supplementary articles');
    }
  }

  /**
   * Update article mapping
   */
  async updateArticleMapping(mappingId: number, request: UpdateArticleMappingRequest): Promise<EpisodeArticleMapping> {
    try {
      const response = await api.put(`${this.API_BASE}/episodes/articles/${mappingId}`, request);
      return response.data.mapping;
    } catch (error) {
      console.error('Error updating article mapping:', error);
      throw new Error('Failed to update article mapping');
    }
  }

  /**
   * Remove article from episode
   */
  async removeArticleFromEpisode(mappingId: number): Promise<void> {
    try {
      await api.delete(`${this.API_BASE}/episodes/articles/${mappingId}`);
    } catch (error) {
      console.error('Error removing article from episode:', error);
      throw new Error('Failed to remove article from episode');
    }
  }

  /**
   * Reorder articles in episode
   */
  async reorderEpisodeArticles(episodeId: number, request: ReorderArticlesRequest): Promise<void> {
    try {
      await api.put(`${this.API_BASE}/episodes/${episodeId}/articles/reorder`, request);
    } catch (error) {
      console.error('Error reordering episode articles:', error);
      throw new Error('Failed to reorder episode articles');
    }
  }

  /**
   * Set main article for episode
   */
  async setMainArticle(episodeId: number, mappingId: number): Promise<void> {
    try {
      await api.put(`${this.API_BASE}/episodes/${episodeId}/articles/${mappingId}/main`);
    } catch (error) {
      console.error('Error setting main article:', error);
      throw new Error('Failed to set main article');
    }
  }

  /**
   * Search articles across episodes
   */
  async searchEpisodeArticles(searchText: string): Promise<{ articles: EpisodeArticleMapping[], count: number }> {
    try {
      const response = await api.get(`${this.API_BASE}/articles/search`, { 
        params: { searchText } 
      });
      return response.data;
    } catch (error) {
      console.error('Error searching episode articles:', error);
      throw new Error('Failed to search episode articles');
    }
  }
}

// Export singleton instance
export const webSeriesService = new WebSeriesService();
export default webSeriesService;